import type { ReactNode } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import { XIcon } from '@/components/layout/icons';

type Props = {
  readonly name: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
};

/**
 * The machine's side panel (OPS-67): 760px from the right, laid OVER the grid
 * rather than beside it — no docking, no backdrop, so the strip of grid left
 * of it stays clickable and swaps the machine. Narrower than that, it takes
 * the full width. Anatomy follows the catalog `SidePanel`; only the width is
 * new.
 *
 * Not a dialog: it does not trap focus or block the page, so it is a labelled
 * `aside`. The confirm dialogs it opens stack above it (`z-50`).
 */
export function MachinePanel({ name, onClose, children }: Props) {
  const t = useTranslations('pages.admin.fleet');

  return (
    <aside
      aria-label={name}
      className="absolute inset-y-0 right-0 z-10 flex w-full max-w-[760px] flex-col border-l border-border bg-surface shadow-xl"
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-neutral-100 px-6 py-4">
        <h2 className="min-w-0 flex-1 truncate text-lg font-semibold text-text-primary">{name}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('panel-close')}
          title={t('panel-close')}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-text-tertiary hover:bg-hover-bg"
        >
          <XIcon className="h-4 w-4" aria-hidden />
        </button>
      </div>
      {/* One scroll context, the panel's own. */}
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
    </aside>
  );
}
